// (2026-05-22) Sección de correlativos fiscales (correlativos_sistema)
// dentro de la ficha de la empresa. Permite configurar el "Nº Factura
// Actual" (próximo número a emitir) por tipo de documento (FC, NC, ND).
//
// Sin un correlativo activo, la conversión de proforma a factura falla
// (asignar_siguiente_numero_fiscal no encuentra rango disponible).
//
// (2026-05-22) El guardado NO es autónomo: se expone vía ref con el
// método `guardar(empresaId)` y el formulario de la empresa lo dispara
// junto con la acción de crear/actualizar la empresa. serial,
// numero_inicial y numero_final están ocultos en la UI — el backend los
// autocompleta cuando no se envían.
import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  getCorrelativosEmpresa,
  saveCorrelativosEmpresa,
} from "../../controllers/api_clients";

// (2026-08-02, ítem 22) Los SEIS tipos con correlativo fiscal propio. Los tres
// últimos ya consumían `correlativos_sistema` al emitir (guías:
// documentos_routes.py:401-413; retenciones: retencion_routes.py:288-301) pero
// no había forma de configurarlos: agujero real, no restricción de diseño. El
// backend los acepta desde esta ronda (empresa_routes.py:103-122,
// `_TABLAS_CON_CORRELATIVO`), resolviendo el codigo_interno del Catálogo 1.
// (2026-08-05) Se retiró el flag `opcional` y su distintivo en la tabla: era
// PURAMENTE visual (quién nace con 1 lo decide TIPOS_BASE, más abajo), así que
// dejarlo habría sido dato muerto. El comportamiento no cambia.
const TIPOS = [
  { tipo: "FC", label: "Factura" },
  { tipo: "NC", label: "Nota de Crédito" },
  { tipo: "ND", label: "Nota de Débito" },
  { tipo: "GUIA", label: "Guía de Despacho / Orden de Entrega" },
  { tipo: "RET_IVA", label: "Retención IVA" },
  { tipo: "RET_ISLR", label: "Retención ISLR" },
];

// Tipos "base" (los que toda empresa emite): si no existen en BD nacen con 1.
const TIPOS_BASE = ["FC", "NC", "ND"];

// `_existente` = la fila vino de la BD (su valor MANDA siempre).
// `_tocado`    = el usuario editó el campo en esta sesión.
const filaVacia = (tipo, numeroActual = "") => ({
  tipo_documento: tipo,
  serial: "",
  numero_inicial: "",
  numero_final: "",
  numero_actual: numeroActual,
  estatus: "activo",
  _existente: false,
  _tocado: false,
});

// (ítem 22, parte 1) Estado inicial: los correlativos nuevos nacen en 1. Esto
// SOLO aplica a filas que no existen en BD; una fila cargada de BD conserva
// SIEMPRE su valor (ver la carga).
const filasIniciales = (numeroActualPorDefecto) =>
  TIPOS.reduce((acc, { tipo }) => {
    acc[tipo] = filaVacia(tipo, numeroActualPorDefecto);
    return acc;
  }, {});

// `onBloqueoChange(bloqueado, motivo)`: (remediación FE-A, P1-4) informa al
// formulario padre de que los correlativos NO se pueden guardar (carga en curso
// o fallida) para que deshabilite su botón de envío. Sin esto, un submit
// disparado DURANTE la carga enviaba las filas por defecto (numero_actual=1) y
// RETROCEDÍA la numeración fiscal real de la empresa.
function CorrelativosFiscales({ empresaId, soloLectura = false, onBloqueoChange = null }, ref) {
  // En creación (sin empresaId) no hay nada que cargar: todas las filas nacen
  // con 1. En edición se parte también de 1 y la carga sobrescribe con los
  // valores reales de la BD.
  const [filas, setFilas] = useState(() => filasIniciales(1));
  const [loading, setLoading] = useState(true);
  // (ítem 22, RIESGO P0) Si la CARGA falla no se puede guardar NADA: enviar un
  // "1 por defecto" sobre una empresa cuyo correlativo real es 5.234 lo
  // RETROCEDERÍA y rompería la numeración fiscal. Antes esto solo hacía
  // console.warn. Ahora bloquea el guardado y se muestra en la UI.
  const [cargaError, setCargaError] = useState(null);

  // Carga inicial de los correlativos existentes. En modo creación no hay
  // empresaId todavía: se parte de las filas por defecto (1) y el usuario las ajusta.
  useEffect(() => {
    if (!empresaId) { setLoading(false); return; }
    let cancelado = false;
    (async () => {
      try {
        setLoading(true);
        setCargaError(null);
        const data = await getCorrelativosEmpresa(empresaId);
        if (cancelado) return;
        const base = filasIniciales(1);
        (data || []).forEach((r) => {
          const t = String(r.tipo_documento || "").toUpperCase();
          if (base[t]) {
            base[t] = {
              tipo_documento: t,
              serial: r.serial ?? "",
              numero_inicial: r.numero_inicial ?? "",
              numero_final: r.numero_final ?? "",
              // El valor de BD MANDA siempre (incluido el 0 o el vacío):
              // nunca se sustituye por el default de la fila nueva.
              numero_actual: r.numero_actual ?? "",
              estatus: r.estatus || "activo",
              _existente: true,
              _tocado: false,
            };
          }
        });
        setFilas(base);
      } catch (e) {
        if (!cancelado) {
          console.warn("No se pudieron cargar los correlativos", e);
          setCargaError(
            (e?.response?.data?.error || e?.message || "error desconocido")
          );
        }
      } finally {
        if (!cancelado) setLoading(false);
      }
    })();
    return () => { cancelado = true; };
  }, [empresaId]);

  // (P1-4) Motivo de bloqueo del guardado, ÚNICA fuente de verdad para el
  // guard de `guardar()`, para la UI y para el aviso al formulario padre.
  const motivoBloqueo = loading
    ? "Los correlativos fiscales aún se están cargando."
    : (cargaError
        ? "No se pudieron leer los correlativos actuales de la empresa (" + cargaError + ")."
        : null);

  useEffect(() => {
    if (typeof onBloqueoChange === 'function') {
      onBloqueoChange(!!motivoBloqueo, motivoBloqueo);
    }
    // (onBloqueoChange fuera de deps a propósito: el padre pasa una arrow nueva
    // en cada render y meterla en deps causaría bucle. La config de eslint del
    // proyecto no registra react-hooks/exhaustive-deps, así que un disable
    // explícito de esa regla ROMPE el build de CRA — no añadirlo.)
  }, [motivoBloqueo]);

  const setCampo = (tipo, campo, valor) => {
    setFilas((prev) => ({
      ...prev,
      [tipo]: { ...prev[tipo], [campo]: valor, _tocado: true },
    }));
  };

  // Guardado unificado: el formulario de la empresa invoca este método
  // tras crear/actualizar la empresa, pasando el id ya conocido. Lanza
  // una excepción si el backend rechaza los datos (el padre la captura).
  useImperativeHandle(ref, () => ({
    async guardar(empresaIdReal) {
      const idDestino = empresaIdReal || empresaId;
      if (!idDestino || soloLectura) return;
      // (ítem 22, RIESGO P0 + remediación FE-A P1-4) Con la carga EN CURSO o
      // FALLIDA no se envía nada: lo que hay en pantalla son defaults, no los
      // correlativos reales. Enviarlos retrocedería la numeración fiscal.
      if (motivoBloqueo) {
        throw new Error(
          motivoBloqueo + " No se guardó ningún correlativo para no sobrescribir " +
          "la numeración fiscal vigente. Reintente en unos segundos."
        );
      }
      const esCreacion = !empresaId; // aún no existía la empresa al montar
      // Filas a enviar:
      //   - Tipos BASE (FC/NC/ND): cualquier fila con valor (el de BD o el
      //     default 1 de una empresa que aún no lo tenía configurado).
      //   - Tipos OPCIONALES (GUIA/RET_IVA/RET_ISLR): solo si ya existían en
      //     BD, si el usuario los editó, o si se está CREANDO la empresa.
      const aEnviar = Object.values(filas).filter((f) => {
        if (String(f.numero_actual ?? "").trim() === "") return false;
        if (TIPOS_BASE.includes(f.tipo_documento)) return true;
        return f._existente || f._tocado || esCreacion;
      });
      if (aEnviar.length === 0) return; // nada configurado: no es error
      for (const f of aEnviar) {
        const act = Number(f.numero_actual);
        if (!Number.isInteger(act) || act < 1) {
          throw new Error(
            `[${f.tipo_documento}] El Nº Factura Actual debe ser un entero ≥ 1.`
          );
        }
      }
      // serial / numero_inicial / numero_final están ocultos en la UI:
      // se reenvían solo si el correlativo ya los tenía cargados; si están
      // vacíos se omiten para que el backend aplique sus valores por
      // defecto (serial existente o '0', inicial 1, final 99999999).
      const payload = aEnviar.map((f) => {
        const item = {
          tipo_documento: f.tipo_documento,
          numero_actual: Number(f.numero_actual),
          estatus: f.estatus || "activo",
        };
        if (String(f.serial ?? "").trim() !== "") {
          item.serial = String(f.serial).trim();
        }
        if (String(f.numero_inicial ?? "").trim() !== "") {
          item.numero_inicial = Number(f.numero_inicial);
        }
        if (String(f.numero_final ?? "").trim() !== "") {
          item.numero_final = Number(f.numero_final);
        }
        return item;
      });
      await saveCorrelativosEmpresa(idDestino, payload);
    },
  }), [filas, empresaId, soloLectura, motivoBloqueo]);

  return (
    <div className="w-full px-4 mt-3">
      <div className="bg-twilight-indigo-50 border border-twilight-indigo-200 rounded p-3">
        <h6 className="text-twilight-indigo-700 text-xs uppercase font-bold mb-1">
          Correlativos fiscales (SENIAT)
        </h6>
        <p className="text-xs text-slate-500 mb-3">
          Número actual autorizado por tipo de documento. Necesario para
          emitir facturas — sin un correlativo <b>activo</b> la conversión de
          proforma a factura no es posible. Se guardan junto con la empresa.
        </p>

        {/* (ítem 22) La carga falló: se BLOQUEA el guardado de correlativos. */}
        {!loading && cargaError && (
          <div className="bg-red-50 border border-red-300 text-red-700 rounded p-2 mb-3 text-xs">
            <i className="fas fa-triangle-exclamation mr-1" />
            <b>No se pudieron leer los correlativos actuales de la empresa</b> ({cargaError}).
            Los valores mostrados son solo valores por defecto, NO los reales. Para no
            retroceder la numeración fiscal, los correlativos <b>no se guardarán</b> en
            esta operación. Reintente cuando el servicio responda.
          </div>
        )}

        {loading ? (
          <p className="text-xs text-slate-400 py-2">Cargando correlativos…</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-blueGray-100 text-blueGray-700">
                  <tr>
                    <th className="text-left py-2 px-2">Tipo</th>
                    <th className="text-right py-2 px-2">Nº Factura Actual</th>
                    <th className="text-left py-2 px-2">Estatus</th>
                  </tr>
                </thead>
                <tbody>
                  {TIPOS.map(({ tipo, label }) => {
                    const f = filas[tipo];
                    return (
                      <tr key={tipo} className="border-t">
                        <td className="py-2 px-2 font-semibold text-blueGray-700">
                          {label}
                          <span className="text-blueGray-400 font-normal"> ({tipo})</span>
                        </td>
                        <td className="py-2 px-2 text-right">
                          <input
                            type="number" min="1"
                            className="border p-1.5 rounded w-24 text-sm text-right disabled:bg-slate-100"
                            value={f.numero_actual}
                            disabled={soloLectura || !!motivoBloqueo}
                            onChange={(e) => setCampo(tipo, "numero_actual", e.target.value)}
                          />
                        </td>
                        <td className="py-2 px-2">
                          <select
                            className="border p-1.5 rounded text-sm disabled:bg-slate-100"
                            value={f.estatus}
                            disabled={soloLectura || !!motivoBloqueo}
                            onChange={(e) => setCampo(tipo, "estatus", e.target.value)}
                          >
                            <option value="activo">Activo</option>
                            <option value="inactivo">Inactivo</option>
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              El <b>Nº Factura Actual</b> es el próximo número a emitir para
              cada tipo de documento. Se registra al guardar la empresa. Las
              guías de despacho y los comprobantes de retención solo se
              registran si les asigna un valor o si la empresa se está creando.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default forwardRef(CorrelativosFiscales);
